/**
 * Club Onboarding — guided flow to recommend the right Club tier.
 * Progressive disclosure: org type → goal → size → recommendation.
 * No backend calls. No new dependencies.
 */

import { useState } from 'react';
import { View, Text, ScrollView, Pressable, SafeAreaView } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { ArrowLeft, Building2 } from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import { startPurchase } from '@/lib/purchases';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { reconcileClubOrg } from '@/lib/orgProvisioning';

// ─── Tier recommendation logic ───────────────────────────────────────────────

type OrgType = 'gym' | 'school' | 'team' | 'other';
type OrgGoal = 'track' | 'compete' | 'events' | 'analyze';
type OrgSize = 'small' | 'medium' | 'large' | 'enterprise';

interface ClubAnswers {
  orgType?: OrgType;
  goal?: OrgGoal;
  size?: OrgSize;
}

interface TierRecommendation {
  id: string;
  name: string;
  price: number;
  tagline: string;
  features: string[];
}

function recommendClubTier(answers: ClubAnswers): TierRecommendation {
  const { orgType, goal, size } = answers;

  // Enterprise: large org or analyze goal
  if (size === 'enterprise' || (size === 'large' && goal === 'analyze')) {
    return {
      id: 'club_elite',
      name: 'Club Elite',
      price: 999,
      tagline: 'For large organizations with advanced analytics needs.',
      features: [
        'Unlimited members',
        'Multi-location management',
        'Enterprise data API',
        'White-label branding',
        'Dedicated support',
        'Custom reporting',
      ],
    };
  }

  // Performance: medium org, events or compete
  if (size === 'large' || (size === 'medium' && (goal === 'compete' || goal === 'events'))) {
    return {
      id: 'club_performance',
      name: 'Club Performance',
      price: 700,
      tagline: 'For growing organizations running competitions and events.',
      features: [
        'Up to 500 members',
        'Competition management',
        'Team leaderboards',
        'TV Mode for gym displays',
        'Coach dashboard',
        'Advanced analytics',
      ],
    };
  }

  // Growth: medium org, track or general
  if (size === 'medium' || (orgType === 'school' && goal === 'track')) {
    return {
      id: 'club_growth',
      name: 'Club Growth',
      price: 499,
      tagline: 'For established gyms and school programs tracking member progress.',
      features: [
        'Up to 150 members',
        'Gym leaderboard',
        'Coach tools',
        'Member progress tracking',
        'Team challenges',
        'Basic analytics',
      ],
    };
  }

  // Starter: default / small
  return {
    id: 'club_starter',
    name: 'Club Starter',
    price: 199,
    tagline: 'Perfect for small gyms and teams getting started with competitive tracking.',
    features: [
      'Up to 50 members',
      'Gym leaderboard',
      'Basic coach tools',
      'Member invites',
      'Monthly reports',
    ],
  };
}

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = ['orgType', 'goal', 'size'] as const;

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function ClubOnboarding() {
  const router = useRouter();
  const { theme } = useTheme();
  const { session } = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { tier } = useSubscription();
  const c = theme.colors;

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<ClubAnswers>({});
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const userId = session?.user?.id ?? '';

  const handleAnswer = (key: keyof ClubAnswers, value: string) => {
    const updated = { ...answers, [key]: value as any };
    setAnswers(updated);
    if (step < STEPS.length - 1) {
      setTimeout(() => setStep(step + 1), 200);
    } else {
      setTimeout(() => setShowResult(true), 200);
    }
  };

  const recommendation = showResult ? recommendClubTier(answers) : null;

  const handlePurchase = async () => {
    if (!recommendation || !userId) return;
    setLoading(true);
    try {
      const purchased = await startPurchase(recommendation.id, userId, 'monthly');
      if (purchased) {
        // Client-side reconciliation: ensure org is provisioned after purchase.
        // Primary provisioning happens via the revenuecat-webhook edge function,
        // but we run a client-side fallback immediately in case the webhook lags.
        await reconcileClubOrg(userId, 'org_pro');
        // Navigate to first-run club setup
        router.push('/club-setup' as any);
      }
    } catch {}
    setLoading(false);
  };

  const renderStep = () => {
    if (step === 0) return (
      <StepCard
        title="What type of organization are you?"
        options={[
          { label: '🏋️ Gym / Fitness Studio', value: 'gym' },
          { label: '🏫 School / Athletic Department', value: 'school' },
          { label: '⚽ Sports Team / Club', value: 'team' },
          { label: '🏢 Other Organization', value: 'other' },
        ]}
        onSelect={(v) => handleAnswer('orgType', v)}
        selected={answers.orgType}
        c={c}
      />
    );

    if (step === 1) return (
      <StepCard
        title="What's your primary goal?"
        options={[
          { label: '📊 Track member progress', value: 'track' },
          { label: '⚔️ Run competitions & duels', value: 'compete' },
          { label: '🏆 Organize events & leagues', value: 'events' },
          { label: '📈 Performance analytics', value: 'analyze' },
        ]}
        onSelect={(v) => handleAnswer('goal', v)}
        selected={answers.goal}
        c={c}
      />
    );

    if (step === 2) return (
      <StepCard
        title="How large is your organization?"
        options={[
          { label: '🌱 Small (under 50 members)', value: 'small' },
          { label: '📈 Medium (50–150 members)', value: 'medium' },
          { label: '🏟️ Large (150–500 members)', value: 'large' },
          { label: '🏢 Enterprise (500+ members)', value: 'enterprise' },
        ]}
        onSelect={(v) => handleAnswer('size', v)}
        selected={answers.size}
        c={c}
      />
    );

    return null;
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }}>
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 }}>
        <Pressable
          onPress={() =>
            showResult
              ? setShowResult(false)
              : step > 0
              ? setStep(step - 1)
              : router.back()
          }
          hitSlop={8}
        >
          <ArrowLeft size={22} color={c.text} />
        </Pressable>
        <Text style={{ fontSize: 18, fontFamily: TYPO.heading.bold, color: c.text }}>
          {showResult ? 'Your Recommendation' : 'Find Your Plan'}
        </Text>
      </View>

      {/* Progress bar */}
      {!showResult && (
        <View style={{ flexDirection: 'row', gap: 4, paddingHorizontal: 16, marginBottom: 24 }}>
          {STEPS.map((_, i) => (
            <View
              key={i}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                backgroundColor: i <= step ? c.accent : c.border,
              }}
            />
          ))}
        </View>
      )}

      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        {showResult && recommendation ? (
          // Result screen
          <View>
            <View
              style={{
                backgroundColor: c.surface,
                borderRadius: 8,
                borderWidth: 1.5,
                borderColor: c.accent,
                padding: 20,
                marginBottom: 16,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Building2 size={20} color={c.accent} />
                <Text
                  style={{
                    fontSize: 11,
                    color: c.accent,
                    fontFamily: TYPO.heading.bold,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                  }}
                >
                  Recommended
                </Text>
              </View>
              <Text
                style={{ fontSize: 24, fontFamily: TYPO.heading.bold, color: c.text, marginBottom: 4 }}
              >
                {recommendation.name}
              </Text>
              <Text style={{ fontSize: 28, fontFamily: TYPO.heading.bold, color: c.text }}>
                ${recommendation.price}
                <Text
                  style={{ fontSize: 16, color: c.textSecondary, fontFamily: TYPO.body.regular }}
                >
                  /mo
                </Text>
              </Text>
              <Text
                style={{
                  color: c.textSecondary,
                  fontSize: 14,
                  marginTop: 8,
                  marginBottom: 16,
                  lineHeight: 20,
                }}
              >
                {recommendation.tagline}
              </Text>
              {recommendation.features.map((f) => (
                <View
                  key={f}
                  style={{ flexDirection: 'row', gap: 8, alignItems: 'center', marginBottom: 6 }}
                >
                  <Text style={{ color: c.accent }}>✓</Text>
                  <Text style={{ color: c.text, fontSize: 14 }}>{f}</Text>
                </View>
              ))}
            </View>

            <Pressable
              onPress={handlePurchase}
              disabled={loading}
              style={{
                backgroundColor: c.accent,
                borderRadius: 4,
                padding: 16,
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <Text
                style={{ color: c.accentForeground, fontFamily: TYPO.heading.bold, fontSize: 16 }}
              >
                {loading ? 'Starting trial...' : 'Start 7-Day Free Trial'}
              </Text>
            </Pressable>

            <Pressable
              onPress={() => {
                setStep(0);
                setAnswers({});
                setShowResult(false);
              }}
              style={{ padding: 12, alignItems: 'center' }}
            >
              <Text style={{ color: c.textSecondary, fontSize: 14 }}>Start over</Text>
            </Pressable>

            <Pressable
              onPress={() => router.push('/paywall' as any)}
              style={{ padding: 12, alignItems: 'center' }}
            >
              <Text style={{ color: c.textTertiary, fontSize: 13 }}>Compare all plans</Text>
            </Pressable>
          </View>
        ) : (
          renderStep()
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

// ─── Step Card Component ───────────────────────────────────────────────────────

function StepCard({
  title,
  options,
  onSelect,
  selected,
  c,
}: {
  title: string;
  options: { label: string; value: string }[];
  onSelect: (v: string) => void;
  selected?: string;
  c: any;
}) {
  return (
    <View>
      <Text
        style={{
          fontSize: 22,
          fontFamily: TYPO.heading.bold,
          color: c.text,
          marginBottom: 24,
          lineHeight: 30,
        }}
      >
        {title}
      </Text>
      {options.map((opt) => (
        <Pressable
          key={opt.value}
          onPress={() => onSelect(opt.value)}
          style={{
            backgroundColor: selected === opt.value ? c.accent + '20' : c.surface,
            borderWidth: 1.5,
            borderColor: selected === opt.value ? c.accent : c.border,
            borderRadius: 4,
            padding: 16,
            marginBottom: 10,
          }}
        >
          <Text
            style={{
              fontSize: 16,
              color: c.text,
              fontFamily:
                selected === opt.value ? TYPO.body.semibold : TYPO.body.regular,
            }}
          >
            {opt.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );
}
